import React from 'react';
import { UserCheck, Clock, MessageCircle, MessageSquare, CheckCircle2, CalendarClock, Lock, PhoneCall, Mail, UserPlus, PauseCircle, AlertTriangle } from 'lucide-react';

const BADGE_BASE = "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold";

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  'ready to go': {
    label: 'Ready to Go',
    className: `${BADGE_BASE} bg-[#05E0D1]/10 text-[#05E0D1] border-2 border-[#05E0D1]/30`,
  },
  'new': {
    label: 'New',
    className: `${BADGE_BASE} bg-[#1875FF]/10 text-[#1875FF] border-2 border-[#1875FF]/30`,
  },
  'talking': {
    label: 'Talking',
    className: `${BADGE_BASE} bg-[#05E0D1]/10 text-[#05E0D1] border-2 border-[#05E0D1]/30`,
  },
  'follow up': {
    label: 'Follow Up',
    className: `${BADGE_BASE} bg-[#1875FF]/5 text-[#1875FF]/70 border-2 border-[#1875FF]/20`,
  },
  'booked': {
    label: 'Booked',
    className: `${BADGE_BASE} bg-[#05E0D1]/10 text-[#05E0D1] border-2 border-[#05E0D1]/30`,
  },
  'on hold': {
    label: 'On Hold',
    className: `${BADGE_BASE} bg-amber-500/10 text-amber-600 border-2 border-amber-500/20`,
  },
  'closed': {
    label: 'Closed',
    className: `${BADGE_BASE} bg-red-50 text-red-700 border-2 border-red-200`,
  },
};

export function getLeadStatusBadge(status: string | null | undefined, defaultLabel = 'Unknown'): React.ReactElement {
  const key = (status || '').toLowerCase().trim();
  const config = STATUS_CONFIG[key];

  if (config) {
    return <span className={config.className}>{config.label}</span>;
  }

  const fallbackConfig = STATUS_CONFIG[defaultLabel.toLowerCase()];
  if (fallbackConfig) {
    return <span className={fallbackConfig.className}>{fallbackConfig.label}</span>;
  }

  return (
    <span className={`${BADGE_BASE} bg-gray-50 text-gray-700 border-2 border-gray-200`}>
      {defaultLabel}
    </span>
  );
}

const SHARED_STATUSES: Record<string, { icon: React.ReactNode; label: string; bg: string; text: string; border: string }> = {
  'assigned': { icon: <UserCheck size={12} />, label: 'Assigned', bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20' },
  'queued': { icon: <Clock size={12} />, label: 'Queued', bg: 'bg-[#1875FF]/8', text: 'text-[#1875FF]', border: 'border-[#1875FF]/20' },
  'calling': { icon: <PhoneCall size={12} />, label: 'Calling', bg: 'bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10', text: 'text-[#1875FF]', border: 'border-[#1875FF]/25' },
  'emailing': { icon: <Mail size={12} />, label: 'Emailing', bg: 'bg-[#05E0D1]/10', text: 'text-[#0bb5a5]', border: 'border-[#05E0D1]/25' },
  'messaging': { icon: <MessageCircle size={12} />, label: 'Messaging', bg: 'bg-[#05E0D1]/10', text: 'text-[#0bb5a5]', border: 'border-[#05E0D1]/25' },
  'connection pending': { icon: <UserPlus size={12} />, label: 'Connection Pending', bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20' },
  'contacting': { icon: <MessageCircle size={12} />, label: 'Contacting', bg: 'bg-[#05E0D1]/10', text: 'text-[#0bb5a5]', border: 'border-[#05E0D1]/25' },
  'talking': { icon: <MessageSquare size={12} />, label: 'Talking', bg: 'bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10', text: 'text-[#1875FF]', border: 'border-[#1875FF]/25' },
  'chatting': { icon: <MessageSquare size={12} />, label: 'Chatting', bg: 'bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10', text: 'text-[#1875FF]', border: 'border-[#1875FF]/25' },
  'booked': { icon: <CheckCircle2 size={12} />, label: 'Booked', bg: 'bg-[#05E0D1]/12', text: 'text-[#0bb5a5]', border: 'border-[#05E0D1]/30' },
  'follow up': { icon: <CalendarClock size={12} />, label: 'Follow Up', bg: 'bg-[#1875FF]/8', text: 'text-[#1875FF]', border: 'border-[#1875FF]/20' },
  'on hold': { icon: <PauseCircle size={12} />, label: 'On Hold', bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20' },
  'closed': { icon: <Lock size={12} />, label: 'Closed', bg: 'bg-rose-500/10', text: 'text-rose-600', border: 'border-rose-500/20' },
  'msg failed': { icon: <AlertTriangle size={12} />, label: 'Msg Failed', bg: 'bg-[#FF3B3B]/10', text: 'text-[#FF3B3B]', border: 'border-[#FF3B3B]/25' },
};

const FALLBACK_CONFIG = { icon: null, label: 'Unknown', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };

export function getCampaignLeadStatusTag(status: string): React.ReactElement {
  const config = SHARED_STATUSES[status?.toLowerCase()] || { ...FALLBACK_CONFIG, label: status || 'Unknown' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${config.bg} ${config.text} border ${config.border} shadow-sm`}>
      {config.icon}
      {config.label}
    </span>
  );
}
